import { useState } from "react";
import { NETWORK_LINKS, NETWORK_NODES, NODE_TYPES } from "@/data/network";
import { Corners, StatusDot, Tag } from "@/components/ui-kit";

const TYPE_COLORS = {
  AGENT: "#43D9FF",
  DRONE: "#00A8FF",
  ROVER: "#1688FF",
  HUMANOID: "#C9D2DC",
  INDUSTRIAL: "#7B4DFF",
  SERVICE: "#00FF9D",
};

const NetworkGraph = () => {
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);
  const byId = Object.fromEntries(NETWORK_NODES.map((n) => [n.id, n]));
  const visible = (n) => filter === "ALL" || n.type === filter;

  return (
    <div data-testid="network-graph" className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="flex flex-wrap gap-2 mb-4">
          {["ALL", ...NODE_TYPES].map((t) => (
            <button
              key={t}
              data-testid={`filter-${t.toLowerCase()}`}
              data-cursor="SCAN"
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] border transition-colors duration-200 ${
                filter === t ? "border-arc-electric text-arc-cyan bg-arc-blue/10" : "border-arc-silver/20 text-arc-silver/50 hover:border-arc-electric/50 hover:text-arc-silver"
              }`}
              aria-pressed={filter === t}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="hud-panel relative p-2 sm:p-4">
          <Corners />
          <svg viewBox="0 0 1000 560" className="w-full h-auto" role="img" aria-label="Simulated ARCMECH machine network map">
            <defs>
              <radialGradient id="nodeGlow"><stop offset="0%" stopColor="#00A8FF" stopOpacity="0.5" /><stop offset="100%" stopColor="#00A8FF" stopOpacity="0" /></radialGradient>
            </defs>
            {NETWORK_LINKS.map(([a, b]) => {
              const na = byId[a]; const nb = byId[b];
              const dim = !(visible(na) && visible(nb));
              return (
                <line
                  key={`${a}-${b}`}
                  x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                  stroke={dim ? "rgba(201,210,220,0.06)" : "rgba(0,168,255,0.35)"}
                  strokeWidth="1"
                  className={dim ? "" : "anim-dash"}
                />
              );
            })}
            {NETWORK_NODES.map((n) => {
              const dim = !visible(n);
              const isActive = active && active.id === n.id;
              return (
                <g
                  key={n.id}
                  opacity={dim ? 0.12 : 1}
                  onMouseEnter={() => setActive(n)}
                  onFocus={() => setActive(n)}
                  onClick={() => setActive(n)}
                  tabIndex={dim ? -1 : 0}
                  role="button"
                  aria-label={`${n.id} ${n.type} node`}
                  data-testid={`node-${n.id.toLowerCase()}`}
                  data-cursor="SCAN"
                  style={{ cursor: "pointer", outline: "none" }}
                >
                  <circle cx={n.x} cy={n.y} r="26" fill="url(#nodeGlow)" opacity={isActive ? 1 : 0.4} />
                  <circle cx={n.x} cy={n.y} r="13" fill="none" stroke={TYPE_COLORS[n.type]} strokeOpacity="0.5" strokeWidth="1" />
                  <circle cx={n.x} cy={n.y} r={isActive ? 8 : 6} fill={TYPE_COLORS[n.type]} />
                  <text x={n.x} y={n.y + 32} textAnchor="middle" fill={dim ? "#556" : "#C9D2DC"} fontSize="11" fontFamily="JetBrains Mono, monospace">{n.id}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
          {NODE_TYPES.map((t) => (
            <span key={t} className="font-mono text-[10px] tracking-[0.2em] text-arc-silver/50 flex items-center gap-2">
              <span className="w-2 h-2 inline-block" style={{ background: TYPE_COLORS[t] }} />{t}
            </span>
          ))}
        </div>
      </div>
      <aside className="hud-panel relative p-5 self-start lg:sticky lg:top-24" data-testid="node-inspector" aria-live="polite">
        <Corners />
        {active ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-sm text-arc-white tracking-wider">{active.id}</div>
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-arc-silver/60">
                <StatusDot status={active.status} />{active.status}
              </div>
            </div>
            <div className="space-y-2.5 font-mono text-xs">
              <div className="flex justify-between"><span className="text-arc-silver/50">MACHINE ID</span><span className="text-arc-silver">{active.id}</span></div>
              <div className="flex justify-between"><span className="text-arc-silver/50">TYPE</span><span style={{ color: TYPE_COLORS[active.type] }}>{active.type}</span></div>
              <div className="flex justify-between"><span className="text-arc-silver/50">REPUTATION</span><span className="text-arc-green">{active.reputation}</span></div>
              <div className="flex justify-between"><span className="text-arc-silver/50">TASKS</span><span className="text-arc-silver">{active.tasks}</span></div>
            </div>
            <div className="mt-4">
              <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mb-2">CAPABILITIES</div>
              <div className="flex flex-wrap gap-1.5">
                {active.capabilities.map((c) => <Tag key={c}>{c}</Tag>)}
              </div>
            </div>
            <div className="mt-5 font-mono text-[9px] tracking-[0.2em] text-arc-silver/30">SIMULATED NODE DATA — INTERFACE DEMO</div>
          </>
        ) : (
          <div className="font-mono text-xs text-arc-silver/40 leading-relaxed">
            &gt; HOVER A NODE TO INSPECT<br /><br />
            MACHINE ID · TYPE · CAPABILITIES<br />
            STATUS · REPUTATION · TASKS<br /><br />
            <span className="text-arc-silver/30">ALL VALUES ARE SIMULATED DEMO DATA.</span>
          </div>
        )}
      </aside>
    </div>
  );
};

export default NetworkGraph;
